/**
 * @fileoverview Admin Leaderboard Page
 * Three sections:
 *  1. Elo System Configuration — editable settings stored in game_config/elo_settings
 *  2. Per-Game Leaderboard    — dropdown of finalised games → ranked entry list
 *  3. Global Elo Leaderboard  — top 100 players by eloRating across all games
 */

import React, { useState, useEffect } from 'react';
import {
  collection, doc, getDoc, getDocs, setDoc, query,
  where, orderBy, limit,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { AdminLayout } from '../../components/admin/AdminLayout';
import './AdminLeaderboardPage.css';

// ─── Shared constants ──────────────────────────────────────────────────────────

const ELO_DEFAULTS = {
  startingRating:         1000,
  kBase:                  32,
  minGamesForRanking:     5,
  minPlayersForElo:       5,
  perfMultiplierExponent: 0.35,
  provisionalThreshold:   10,
};

/**
 * Tiered placement points curve — must match the Cloud Function exactly.
 */
function getPlacementPoints(rank) {
  if (rank < 1)    return 0;
  if (rank <= 10)  return 1000 - (rank - 1)  * 20;
  if (rank <= 30)  return 805  - (rank - 11) * 15;
  if (rank <= 60)  return 510  - (rank - 31) * 10;
  if (rank <= 100) return 215  - (rank - 61) * 5;
  return 0;
}

function getOrdinalSuffix(n) {
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// ─── Section 1: Elo Configuration Panel ───────────────────────────────────────

function EloConfigPanel() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'game_config', 'elo_settings')).then(snap => {
      setConfig(snap.exists() ? { ...ELO_DEFAULTS, ...snap.data() } : { ...ELO_DEFAULTS });
    });
  }, []);

  async function handleSave() {
    if (!config) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'game_config', 'elo_settings'), config, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  if (!config) return <div className="ss-lb-loading">Loading Elo configuration…</div>;

  const previewRanks = [1, 5, 10, 11, 20, 30, 31, 45, 60, 61, 80, 100];

  return (
    <section className="ss-lb-card">
      <div className="ss-lb-card-header">
        <h2 className="ss-lb-card-title">⚡ Elo Rating System Configuration</h2>
        <p className="ss-lb-card-subtitle">
          Changes apply to future finalisations only — existing ratings are not retroactively adjusted.
        </p>
      </div>

      <div className="ss-elo-config-grid">

        <div className="ss-elo-field">
          <label className="ss-elo-label">STARTING RATING (new players)</label>
          <p className="ss-elo-helper">
            Provisional rating assigned before a player's first game.
          </p>
          <input
            type="number" min="100" max="2000" step="50"
            className="ss-elo-input"
            value={config.startingRating}
            onChange={e => setConfig({ ...config, startingRating: parseInt(e.target.value) || 1000 })}
          />
        </div>

        <div className="ss-elo-field">
          <label className="ss-elo-label">K-FACTOR BASE — <strong>{config.kBase}</strong></label>
          <p className="ss-elo-helper">
            Controls rating volatility. Higher = bigger swings per game. Recommended: 32.
          </p>
          <div className="ss-slider-row">
            <span className="ss-slider-label">8 (stable)</span>
            <input
              type="range" min="8" max="64" step="4"
              className="ss-slider"
              value={config.kBase}
              onChange={e => setConfig({ ...config, kBase: parseInt(e.target.value) })}
            />
            <span className="ss-slider-label">64 (volatile)</span>
          </div>
        </div>

        <div className="ss-elo-field">
          <label className="ss-elo-label">MIN GAMES FOR GLOBAL RANKING</label>
          <p className="ss-elo-helper">
            Players show as "Provisional" on the global leaderboard until this many games are completed.
          </p>
          <input
            type="number" min="1" max="20"
            className="ss-elo-input"
            value={config.minGamesForRanking}
            onChange={e => setConfig({ ...config, minGamesForRanking: parseInt(e.target.value) || 5 })}
          />
        </div>

        <div className="ss-elo-field">
          <label className="ss-elo-label">MIN FIELD SIZE FOR ELO TO COUNT</label>
          <p className="ss-elo-helper">
            Games with fewer players are skipped — prevents rating manipulation from private or test games.
          </p>
          <input
            type="number" min="2" max="20"
            className="ss-elo-input"
            value={config.minPlayersForElo}
            onChange={e => setConfig({ ...config, minPlayersForElo: parseInt(e.target.value) || 5 })}
          />
        </div>

        <div className="ss-elo-field">
          <label className="ss-elo-label">PROVISIONAL PERIOD (games)</label>
          <p className="ss-elo-helper">
            Players in their first <strong>{config.provisionalThreshold ?? 10}</strong> games have
            reduced Elo volatility (dampener ramps from 0.5× at game 1 to 1.0× at the threshold).
            Prevents lucky newcomers from reaching elite ratings on small samples.
          </p>
          <input
            type="number" min="5" max="20"
            className="ss-elo-input"
            value={config.provisionalThreshold ?? 10}
            onChange={e => setConfig({ ...config, provisionalThreshold: parseInt(e.target.value) || 10 })}
          />
        </div>

        <div className="ss-elo-field">
          <label className="ss-elo-label">PERFORMANCE MULTIPLIER EXPONENT — <strong>{config.perfMultiplierExponent ?? 0.35}</strong></label>
          <p className="ss-elo-helper">
            Controls how aggressively earnings dominance (vs field median) affects the K-factor.
            0.35 is recommended — rewards dominant wins without overwhelming placement.
            Lower (e.g. 0.25) = weaker effect. Higher (e.g. 0.45) = stronger effect. Range: 0.5–2.0×.
          </p>
          <div className="ss-slider-row">
            <span className="ss-slider-label">0.10 (weak)</span>
            <input
              type="range" min="0.10" max="0.60" step="0.05"
              className="ss-slider"
              value={config.perfMultiplierExponent ?? 0.35}
              onChange={e => setConfig({ ...config, perfMultiplierExponent: parseFloat(e.target.value) })}
            />
            <span className="ss-slider-label">0.60 (strong)</span>
          </div>
        </div>

      </div>

      {/* Placement Points Curve Preview */}
      <div className="ss-elo-points-preview">
        <h3 className="ss-elo-points-title">Placement Points &amp; K-Factor Preview</h3>
        <table className="ss-elo-points-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Points</th>
              <th>K base</th>
              <th>K × 2.0 (dominant win)</th>
              <th>K × 0.5 (first game)</th>
            </tr>
          </thead>
          <tbody>
            {previewRanks.map(rank => {
              const pts        = getPlacementPoints(rank);
              const kBase_     = config.kBase * (Math.max(pts, 10) / 500);
              const kDominant  = (kBase_ * 2.0).toFixed(1);
              const kFirstGame = (kBase_ * 0.5).toFixed(1);
              const tier       = rank <= 10 ? 'top' : rank <= 30 ? 'upper' : rank <= 60 ? 'lower' : 'bottom';
              return (
                <tr key={rank} className={`ss-tier-${tier}`}>
                  <td>{getOrdinalSuffix(rank)}</td>
                  <td>{pts}</td>
                  <td>{kBase_.toFixed(1)}</td>
                  <td>{kDominant}</td>
                  <td>{kFirstGame}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="ss-elo-points-note">
          "K base" = placement K only. "Dominant win" applies 2.0× perf multiplier (max).
          "First game" applies 0.5× provisional dampener. Actual K is the product of all three.
        </p>
      </div>

      <button
        className={`ss-lb-btn ss-lb-btn--primary ${saving ? 'ss-lb-btn--loading' : ''}`}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving…' : saved ? '✓ Saved!' : '💾 Save Elo Settings'}
      </button>
    </section>
  );
}

// ─── Section 2: Per-Game Leaderboard ──────────────────────────────────────────

function GameLeaderboard() {
  const [games,          setGames]          = useState([]);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [entries,        setEntries]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [gamesLoading,   setGamesLoading]   = useState(true);

  // Load all finalised games for the dropdown
  useEffect(() => {
    const q = query(
      collection(db, 'game_slates'),
      where('finalised', '==', true),
      orderBy('meetingDate', 'desc'),
      limit(50)
    );
    getDocs(q)
      .then(snap => setGames(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(err => console.error('[Leaderboard] Failed to load games:', err))
      .finally(() => setGamesLoading(false));
  }, []);

  // Load entries for the selected game
  useEffect(() => {
    if (!selectedGameId) { setEntries([]); return; }
    setLoading(true);

    getDocs(query(
      collection(db, 'player_entries'),
      where('slateId', '==', selectedGameId)
    ))
    .then(async snap => {
      const rawEntries = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Sort client-side: by finalRank if present, else by totalVirtualEarnings desc
      rawEntries.sort((a, b) => {
        if (a.finalRank && b.finalRank) return a.finalRank - b.finalRank;
        return (b.totalVirtualEarnings || 0) - (a.totalVirtualEarnings || 0);
      });

      // Fetch display names
      const userIds  = [...new Set(rawEntries.map(e => e.userId).filter(Boolean))];
      const userSnaps = await Promise.all(userIds.map(uid => getDoc(doc(db, 'users', uid))));
      const userMap  = {};
      userSnaps.forEach(d => { if (d.exists()) userMap[d.id] = d.data(); });

      setEntries(rawEntries.map((e, i) => ({
        ...e,
        displayRank:  e.finalRank ?? (i + 1),
        displayName:  userMap[e.userId]?.displayName || userMap[e.userId]?.email || 'Unknown',
        eloRating:    userMap[e.userId]?.eloRating ?? '—',
        eloGamesPlayed: userMap[e.userId]?.eloGamesPlayed ?? 0,
      })));
    })
    .catch(err => console.error('[Leaderboard] Failed to load entries:', err))
    .finally(() => setLoading(false));
  }, [selectedGameId]);

  const selectedGame = games.find(g => g.id === selectedGameId);

  const rankIcon = r => r === 1 ? '🥇' : r === 2 ? '🥈' : r === 3 ? '🥉' : null;

  return (
    <section className="ss-lb-card">
      <div className="ss-lb-card-header">
        <h2 className="ss-lb-card-title">📋 Per-Game Leaderboard</h2>
      </div>

      {gamesLoading ? (
        <div className="ss-lb-loading">Loading games…</div>
      ) : (
        <select
          className="ss-lb-game-select"
          value={selectedGameId}
          onChange={e => setSelectedGameId(e.target.value)}
        >
          <option value="">— Select a finalised game —</option>
          {games.length === 0 && (
            <option value="" disabled>No finalised games found</option>
          )}
          {games.map(g => (
            <option key={g.id} value={g.id}>
              {g.gameName} — {g.meetingDate}
              {g.eloFieldSize ? ` (${g.eloFieldSize} players)` : ''}
            </option>
          ))}
        </select>
      )}

      {loading && <div className="ss-lb-loading">Loading results…</div>}

      {!loading && selectedGame && entries.length > 0 && (
        <>
          <div className="ss-lb-game-meta">
            <span className="ss-lb-game-meta__name">{selectedGame.gameName}</span>
            <span className="ss-lb-game-meta__count">{entries.length} players</span>
            <span className={`ss-lb-elo-badge ${selectedGame.eloProcessed ? 'ss-lb-elo-badge--done' : 'ss-lb-elo-badge--pending'}`}>
              {selectedGame.eloProcessed ? '✓ Elo calculated' : '⏳ Elo pending'}
            </span>
          </div>

          <table className="ss-lb-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Player</th>
                <th>Placement Pts</th>
                <th>Final Score</th>
                <th>Current Elo</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr
                  key={entry.id}
                  className={entry.displayRank <= 3 ? 'ss-lb-row--podium' : ''}
                >
                  <td>
                    <span className={`ss-lb-rank ss-lb-rank--${Math.min(entry.displayRank, 4)}`}>
                      {rankIcon(entry.displayRank) || `#${entry.displayRank}`}
                    </span>
                  </td>
                  <td className="ss-lb-player-name">{entry.displayName}</td>
                  <td>{getPlacementPoints(entry.displayRank).toLocaleString('en-AU')}</td>
                  <td>{(entry.totalVirtualEarnings || 0).toLocaleString('en-AU')}</td>
                  <td className="ss-lb-elo-value">
                    {typeof entry.eloRating === 'number'
                      ? entry.eloRating.toLocaleString('en-AU')
                      : entry.eloRating}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {!loading && selectedGameId && entries.length === 0 && (
        <p className="ss-lb-empty">No entries found for this game.</p>
      )}
    </section>
  );
}

// ─── Section 3: Global Elo Leaderboard ────────────────────────────────────────

function GlobalEloLeaderboard() {
  const [players,    setPlayers]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [minGames,   setMinGames]   = useState(5);
  const [error,      setError]      = useState('');

  // Load minGamesForRanking from config
  useEffect(() => {
    getDoc(doc(db, 'game_config', 'elo_settings')).then(snap => {
      if (snap.exists()) setMinGames(snap.data().minGamesForRanking ?? 5);
    });
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, 'users'),
      where('role', '==', 'player'),
      orderBy('eloRating', 'desc'),
      limit(100)
    );
    getDocs(q)
      .then(snap => {
        setPlayers(snap.docs.map((d, i) => ({ id: d.id, rank: i + 1, ...d.data() })));
      })
      .catch(err => {
        console.error('[Leaderboard] Global query failed:', err);
        setError('Could not load global leaderboard. A composite index on users(role, eloRating DESC) may be required — check the browser console for the Firestore index creation link.');
      })
      .finally(() => setLoading(false));
  }, []);

  const rankIcon = r => r === 1 ? '🥇' : r === 2 ? '🥈' : r === 3 ? '🥉' : null;

  if (loading) return (
    <section className="ss-lb-card">
      <div className="ss-lb-loading">Loading global rankings…</div>
    </section>
  );

  return (
    <section className="ss-lb-card">
      <div className="ss-lb-card-header">
        <h2 className="ss-lb-card-title">🌏 Global Elo Leaderboard — Top 100</h2>
        <p className="ss-lb-card-subtitle">
          Players need {minGames}+ completed games for a confirmed rank.
          Provisional players shown in grey.
        </p>
      </div>

      {error && <p className="ss-lb-error">{error}</p>}

      {!error && players.length === 0 && (
        <p className="ss-lb-empty">
          No ranked players yet — ratings will appear after the first finalised game with {minGames}+ players.
        </p>
      )}

      {!error && players.length > 0 && (
        <table className="ss-lb-table ss-lb-table--global">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th>Elo Rating</th>
              <th>Peak</th>
              <th>Games</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {players.map(player => {
              const provisional = (player.eloGamesPlayed ?? 0) < minGames;
              return (
                <tr
                  key={player.id}
                  className={provisional ? 'ss-lb-row--provisional' : ''}
                >
                  <td>
                    {provisional ? (
                      <span className="ss-lb-rank ss-lb-rank--provisional">—</span>
                    ) : (
                      <span className={`ss-lb-rank ss-lb-rank--${Math.min(player.rank, 4)}`}>
                        {rankIcon(player.rank) || `#${player.rank}`}
                      </span>
                    )}
                  </td>
                  <td className="ss-lb-player-name">
                    {player.displayName || player.email || player.id}
                  </td>
                  <td className="ss-lb-elo-value">
                    {(player.eloRating ?? 1000).toLocaleString('en-AU')}
                  </td>
                  <td className="ss-lb-elo-peak">
                    {(player.eloPeak ?? player.eloRating ?? 1000).toLocaleString('en-AU')}
                  </td>
                  <td>{player.eloGamesPlayed ?? 0}</td>
                  <td>
                    <span className={`ss-lb-status-pill ${provisional ? 'ss-lb-status-pill--provisional' : 'ss-lb-status-pill--ranked'}`}>
                      {provisional ? 'Provisional' : 'Ranked'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ─── Page root ─────────────────────────────────────────────────────────────────

export function AdminLeaderboardPage() {
  return (
    <AdminLayout>
      <div className="ss-lb-page">
        <div className="ss-lb-page-header">
          <h1 className="ss-lb-page-title">🏆 Leaderboard</h1>
        </div>
        <EloConfigPanel />
        <GameLeaderboard />
        <GlobalEloLeaderboard />
      </div>
    </AdminLayout>
  );
}

export default AdminLeaderboardPage;
